import React, { useReducer, useEffect, useState } from 'react'

import { CustomTheme } from '@masfront/common-b2c'

import { Box, Button, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import WarningIcon from '@material-ui/icons/Warning'
import { Alert } from '@material-ui/lab'

import { ReactComponent as Success } from './assets/success-icon.svg'
import useChangeEmail from './useChangeEmail'

import { useCustomer } from '~/customer'
import { getEmailFromCustomer, isValidEmail } from '~/customer/helpers/customer.helpers'
import AddEnergyContract from '~/energyContract/components/AddEnergyContract'
import { ContentModal, CustomCircularProgress, LineItem } from '~/ui/components'

const useStyles = makeStyles((theme: CustomTheme) => {
  return {
    inputEmail: {
      '& .Mui-error': {
        color: theme.palette.error.main,
      },
    },
  }
})

enum StateId {
  ADDING_ENERGY_CONTRACTS = 'ADDING_ENERGY_CONTRACTS',
  CHANGING_EMAIL = 'CHANGING_EMAIL',
  CHANGE_EMAIL_LOADING = 'CHANGE_EMAIL_LOADING',
  CHANGED_EMAIL = 'CHANGED_EMAIL',
}

export interface ChangeEmailModalState {
  stateId: StateId
  customerEmail: string
}

enum ActionType {
  ADD_ENERGY_CONTRACT = 'ADD_ENERGY_CONTRACT',
  NEXT_STEP = 'NEXT_STEP',
  GO_BACK = 'GO BACK',
  CHANGE_EMAIL = 'CHANGE_EMAIL',
  ON_CHANGE_EMAIL_LOADING = 'ON_CHANGE_EMAIL_LOADING',
  ON_CHANGE_EMAIL_SUCCESS = 'ON_CHANGE_EMAIL_SUCCESS',
  ON_CHANGE_EMAIL_ERROR = 'ON_CHANGE_EMAIL_ERROR',
}

interface nextStep {
  type: ActionType.NEXT_STEP
}

interface goBack {
  type: ActionType.GO_BACK
}

interface onChangeEmailLoading {
  type: ActionType.ON_CHANGE_EMAIL_LOADING
}

interface onChangeEmailSuccess {
  type: ActionType.ON_CHANGE_EMAIL_SUCCESS
  payload: {
    email: string
  }
}

interface onChangeEmailError {
  type: ActionType.ON_CHANGE_EMAIL_ERROR
}

type ChangeEmailModalActions =
  | nextStep
  | goBack
  | onChangeEmailLoading
  | onChangeEmailSuccess
  | onChangeEmailError

function reducer(state: ChangeEmailModalState, action: ChangeEmailModalActions) {
  switch (state.stateId) {
    case StateId.ADDING_ENERGY_CONTRACTS: {
      switch (action.type) {
        case ActionType.NEXT_STEP: {
          return {
            ...state,
            stateId: StateId.CHANGING_EMAIL,
          }
        }
        default:
          return state
      }
    }
    case StateId.CHANGING_EMAIL: {
      switch (action.type) {
        case ActionType.ON_CHANGE_EMAIL_LOADING:
          return {
            ...state,
            stateId: StateId.CHANGE_EMAIL_LOADING,
          }
        case ActionType.GO_BACK:
          return {
            ...state,
            stateId: StateId.ADDING_ENERGY_CONTRACTS,
          }
        default:
          return state
      }
    }
    case StateId.CHANGE_EMAIL_LOADING: {
      switch (action.type) {
        case ActionType.ON_CHANGE_EMAIL_ERROR:
          return {
            ...state,
            stateId: StateId.CHANGING_EMAIL,
          }
        case ActionType.ON_CHANGE_EMAIL_SUCCESS:
          return {
            ...state,
            stateId: StateId.CHANGED_EMAIL,
            customerEmail: action.payload.email,
          }
        default:
          return state
      }
    }
    case StateId.CHANGED_EMAIL: {
      switch (action.type) {
        case ActionType.GO_BACK:
          return {
            ...state,
            stateId: StateId.ADDING_ENERGY_CONTRACTS,
          }
        default:
          return state
      }
    }

    default:
      return state
  }
}

export interface ChangeEmailModalProps {
  isOpen: boolean
  onClose: (emailHasChanged: boolean) => void
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({ isOpen, onClose }) => {
  const classes = useStyles()
  const { customer } = useCustomer()
  const useChangeEmailSvc = useChangeEmail()

  const initialState = {
    customerEmail: getEmailFromCustomer(customer) || '',
    stateId: StateId.ADDING_ENERGY_CONTRACTS,
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const [newCustomerEmail, setNewCustomerEmail] = useState(state.customerEmail)

  const emailError = !isValidEmail(newCustomerEmail)
  const disabledChangeEmail =
    newCustomerEmail === state.customerEmail || !isValidEmail(newCustomerEmail)

  useEffect(() => {
    switch (state.stateId) {
      case StateId.CHANGING_EMAIL:
        if (useChangeEmailSvc.state.loading) dispatch({ type: ActionType.ON_CHANGE_EMAIL_LOADING })
        break
      default:
    }
  }, [useChangeEmailSvc.state.loading])

  useEffect(() => {
    switch (state.stateId) {
      case StateId.CHANGE_EMAIL_LOADING:
        if (useChangeEmailSvc.state.error) {
          dispatch({
            type: ActionType.ON_CHANGE_EMAIL_ERROR,
          })
        }
        break
      default:
    }
  }, [useChangeEmailSvc.state.error])

  useEffect(() => {
    switch (state.stateId) {
      case StateId.CHANGE_EMAIL_LOADING:
        if (useChangeEmailSvc.state.updatedEmail) {
          dispatch({
            type: ActionType.ON_CHANGE_EMAIL_SUCCESS,
            payload: { email: useChangeEmailSvc.state.updatedEmail },
          })
        }
        break
      default:
    }
  }, [useChangeEmailSvc.state.updatedEmail])

  const onCloseModal = () => {
    const emailHasChanged =
      !!useChangeEmailSvc.state.updatedEmail &&
      useChangeEmailSvc.state.updatedEmail !== getEmailFromCustomer(customer)
    onClose(emailHasChanged)
  }

  function MainModalContentRenderer() {
    switch (state.stateId) {
      case StateId.ADDING_ENERGY_CONTRACTS: {
        return (
          customer && (
            <AddEnergyContract
              customerDocumentValue={customer.documentValue}
              customerEmail={state.customerEmail}
              onClose={onCloseModal}
              nextStep={() => dispatch({ type: ActionType.NEXT_STEP })}
            />
          )
        )
      }
      case StateId.CHANGING_EMAIL: {
        return (
          customer && (
            <Box data-hook="change-email-box">
              <Box my={6}>
                <LineItem
                  dataHook="current-email"
                  primaryText="Email actual"
                  secondaryText={state.customerEmail}
                />
              </Box>
              <Box width={350}>
                <TextField
                  className={classes.inputEmail}
                  name="email"
                  type="text"
                  variant="outlined"
                  error={emailError}
                  fullWidth
                  label="Nuevo email"
                  value={newCustomerEmail}
                  placeholder="cambiar email"
                  onChange={(e) => setNewCustomerEmail(e.target.value)}
                  autoComplete="off"
                  helperText={emailError && 'El email introducido no es válido.'}
                  inputProps={{
                    'data-hook': 'change-email-input',
                  }}
                />
              </Box>
              {useChangeEmailSvc.state.error && (
                <Box my={2} width={350}>
                  <Alert severity="error" icon={<WarningIcon />}>
                    Ha ocurrido un error.
                  </Alert>
                </Box>
              )}
              <Box py={8} display="flex">
                <Box mr={6}>
                  <Button
                    data-hook="save-email-button"
                    variant="contained"
                    color="secondary"
                    disabled={disabledChangeEmail}
                    onClick={() => useChangeEmailSvc.changeEmail(customer.id, newCustomerEmail)}
                  >
                    Guardar
                  </Button>
                </Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    dispatch({ type: ActionType.GO_BACK })
                    setNewCustomerEmail(state.customerEmail)
                    useChangeEmailSvc.clearChangeEmailError()
                  }}
                >
                  Cancelar
                </Button>
              </Box>
            </Box>
          )
        )
      }
      case StateId.CHANGED_EMAIL: {
        return (
          <Box display="flex" alignItems="center" flexDirection="column" py={8}>
            <Success />
            <Box py={8}>
              <Typography variant="h2">El email ha sido cambiado correctamente</Typography>
            </Box>
            <Button
              data-hook="save-email-button"
              variant="contained"
              color="secondary"
              onClick={() => dispatch({ type: ActionType.GO_BACK })}
            >
              Aceptar
            </Button>
          </Box>
        )
      }
      case StateId.CHANGE_EMAIL_LOADING: {
        return (
          <Box m={8}>
            <CustomCircularProgress size={100} />
          </Box>
        )
      }
      default:
        return <></>
    }
  }

  // RENDERING
  switch (state.stateId) {
    default:
      return (
        <ContentModal
          dataHook="change-email-modal"
          isOpen={isOpen}
          onClose={onCloseModal}
          title="Verificar dirección de email"
          onNextStep={() => dispatch({ type: ActionType.NEXT_STEP })}
          withoutButtons
        >
          {MainModalContentRenderer()}
        </ContentModal>
      )
      break
  }
}

export default ChangeEmailModal
